import { zodResolver } from "@hookform/resolvers/zod";
import { type DefaultValues, type FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getFormValuesDiff } from "web_myplann/Portfolio/PortfolioShared/getFormValuesDiff";
import type { z } from "zod";

type MutateFn<TPatch> = (
	data: TPatch,
	options: {
		onSuccess: () => void;
	},
) => void;

type DiffOf<TInput, TOutput> = Partial<
	Pick<TOutput, keyof TInput & keyof TOutput>
>;

type Args<TInput extends FieldValues, TOutput> = {
	schema: z.ZodType<TOutput, z.ZodTypeDef & { typeName: string }, TInput>;
	defaultValues: DefaultValues<TInput>;
	mutate: MutateFn<DiffOf<TInput, TOutput>>;
	successToast: string;
	redirectTo: string;
};

export const useEditPortfolioForm = <TInput extends FieldValues, TOutput>({
	schema,
	defaultValues,
	mutate,
	successToast,
	redirectTo,
}: Args<TInput, TOutput>) => {
	const navigate = useNavigate();

	const form = useForm<TInput, unknown, TOutput>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const onSubmit = form.handleSubmit((data) => {
		const partial = getFormValuesDiff<TInput, TOutput>(
			form.formState.dirtyFields,
			data,
		);

		mutate(partial, {
			onSuccess: () => {
				toast.success(successToast);
				navigate(redirectTo, { replace: true });
			},
		});
	});

	return { form, onSubmit };
};
